import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsApi, ordersApi } from '../services/api';
import Header from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  IndianRupee, FileText, FileCheck, Clock, ChevronRight, TrendingUp, TrendingDown,
  Gem, Package, CalendarDays, Target, ArrowUp, ArrowDown, Flame, Star, AlertTriangle,
  Settings2, Loader2, Zap, Crown, Download, Search, Users
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';

// ─── Helpers ───
const RANGE_OPTIONS = [
  { value: '6', label: '6 Months' },
  { value: '12', label: '1 Year' },
  { value: '24', label: '2 Years' },
];

const formatCurrency = (v) => {
  if (!v) return '0';
  if (v >= 10000000) return `${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(2)} L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString('en-IN');
};

const formatMonth = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(mo,10)-1]} ${y.slice(2)}`;
};

const formatDay = (d) => {
  if (!d) return '';
  const [, mo, day] = d.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${parseInt(day,10)} ${months[parseInt(mo,10)-1]}`;
};

// ─── Animated Progress Ring ───
const ProgressRing = ({ value, max, label, current, target, unit, color, delay = 0 }) => {
  const [animatedPct, setAnimatedPct] = useState(0);
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedPct(pct);
    }, 200 + delay);
    return () => clearTimeout(timer);
  }, [pct, delay]);

  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (animatedPct / 100) * circumference;

  const isOver = pct >= 100;
  const ringColor = isOver ? '#10b981' : color;

  return (
    <div className="flex flex-col items-center gap-2 animate-scale-in" style={{ animationDelay: `${delay}ms` }}>
      <div className="relative w-[130px] h-[130px]">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r={radius} fill="none" stroke="currentColor" strokeWidth="6" className="text-muted/20" />
          <circle
            cx="60" cy="60" r={radius} fill="none"
            stroke={ringColor} strokeWidth="7" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
            className="transition-all duration-[1500ms] ease-out"
            style={{ filter: `drop-shadow(0 0 6px ${ringColor}40)` }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-black tabular-nums" style={{ color: ringColor }}>
            {Math.round(animatedPct)}%
          </span>
          {isOver && <Zap className="h-3.5 w-3.5 text-emerald-500 animate-pulse" />}
        </div>
      </div>
      <div className="text-center">
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
        <p className="text-xs tabular-nums">
          <span className="font-bold">{current}</span>
          <span className="text-muted-foreground"> / {target} {unit}</span>
        </p>
      </div>
    </div>
  );
};

// ─── Growth Indicator ───
const GrowthBadge = ({ value, label }) => {
  const isUp = value > 0;
  const isFlat = value === 0;
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors animate-slide-in-right">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className={cn("flex items-center gap-1 text-xs font-bold tabular-nums",
        isFlat ? "text-muted-foreground" : isUp ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"
      )}>
        {!isFlat && (isUp ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
        {isFlat ? '—' : `${Math.abs(value)}%`}
      </div>
    </div>
  );
};

// ─── Day Highlight Card ───
const DayCard = ({ day, label, icon: Icon, iconColor, metric, metricLabel }) => {
  if (!day?.date) return null;
  return (
    <div className="p-3 rounded-lg border bg-muted/20 hover:bg-muted/40 transition-all animate-slide-in">
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={cn("h-4 w-4", iconColor)} />
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      <p className="text-sm font-bold">{formatDay(day.date)}</p>
      <p className="text-xs text-muted-foreground">{metric} {metricLabel}</p>
    </div>
  );
};

// ─── Chart Tooltip ───
const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white/95 dark:bg-neutral-900/95 backdrop-blur-md border border-slate-200 dark:border-neutral-700 rounded-xl p-3 shadow-2xl text-sm">
      <p className="font-bold text-foreground mb-1.5">{formatter ? formatter(label) : label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 py-0.5">
          <div className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-muted-foreground text-xs">{p.name}:</span>
          <span className="font-bold text-foreground text-xs tabular-nums">
            {p.name === 'Revenue' ? `₹${formatCurrency(p.value)}` : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── Orders by Salesperson Section ───
const OrdersBySalespersonSection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [selectedSp, setSelectedSp] = useState('all');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedSp && selectedSp !== 'all' ? { salesperson: selectedSp } : {};
      const res = await analyticsApi.getSalesOrders(params);
      setOrders(res.data?.orders || []);
      setSalespersons(res.data?.salespersons || []);
    } catch {
      toast.error('Failed to load salesperson orders');
    } finally {
      setLoading(false);
    }
  }, [selectedSp]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = selectedSp && selectedSp !== 'all' ? { salesperson: selectedSp } : {};
      const res = await ordersApi.exportExcel(params);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = selectedSp && selectedSp !== 'all' ? `_${selectedSp.replace(/\s+/g, '_')}` : '';
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
      a.download = `SiddhiJewels_Orders${suffix}_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel export downloaded');
    } catch (err) {
      toast.error(err?.response?.status === 403 ? 'You do not have permission to export' : 'Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const filtered = orders.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (o.co_number || '').toLowerCase().includes(q) ||
      (o.customer_name || '').toLowerCase().includes(q) ||
      (o.branch || '').toLowerCase().includes(q) ||
      (o.salesperson || '').toLowerCase().includes(q)
    );
  });

  // Group by salesperson when "all" is selected
  const grouped = (() => {
    if (selectedSp !== 'all') return null;
    const map = new Map();
    for (const o of filtered) {
      const k = o.salesperson || '(unassigned)';
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(o);
    }
    return Array.from(map.entries()).sort((a, b) => b[1].length - a[1].length);
  })();

  const statusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('dispatch')) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400';
    if (s.includes('ready')) return 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400';
    if (s.includes('floor') || s.includes('casting')) return 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400';
    return 'bg-slate-100 text-slate-700 dark:bg-neutral-800 dark:text-slate-300';
  };

  const renderRow = (o) => (
    <div
      key={o.id}
      onClick={() => navigate(`/orders/${o.id}`)}
      className="grid grid-cols-12 gap-2 px-3 py-2.5 border-b border-border last:border-0 hover:bg-muted/40 cursor-pointer items-center text-xs"
      data-testid={`sp-order-row-${o.co_number}`}
    >
      <div className="col-span-3 md:col-span-2">
        <p className="font-semibold text-sm">{o.co_number}</p>
        <p className="text-[10px] text-muted-foreground">{o.created_at}</p>
      </div>
      <div className="col-span-5 md:col-span-3 min-w-0">
        <p className="font-medium truncate">{o.customer_name}{o.branch ? <span className="text-muted-foreground"> — {o.branch}</span> : ''}</p>
        <p className="text-[10px] text-muted-foreground truncate">{o.factory_name || '—'}</p>
      </div>
      <div className="hidden md:block md:col-span-2 text-muted-foreground truncate">{o.salesperson || '—'}</div>
      <div className="col-span-2 md:col-span-1 text-right tabular-nums font-semibold">{o.pieces_ordered}</div>
      <div className="col-span-2 md:col-span-2">
        <span className={cn("inline-block px-1.5 py-0.5 rounded text-[10px] font-medium", statusBadge(o.status))}>
          {o.status || '—'}
        </span>
      </div>
      <div className="hidden md:block md:col-span-1 text-right text-muted-foreground tabular-nums">
        {o.delivery_date || '—'}
        {typeof o.days_to_delivery === 'number' && (
          <p className={cn("text-[10px]", o.days_to_delivery < 0 ? 'text-red-500' : 'text-muted-foreground')}>
            {o.days_to_delivery < 0 ? `${Math.abs(o.days_to_delivery)}d late` : `${o.days_to_delivery}d`}
          </p>
        )}
      </div>
      <div className="hidden md:flex md:col-span-1 justify-end">
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
    </div>
  );

  return (
    <Card data-testid="orders-by-salesperson-section">
      <CardHeader className="pb-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <Users className="h-4.5 w-4.5 text-white" />
            </div>
            <div>
              <CardTitle className="font-heading text-base">Orders by Salesperson</CardTitle>
              <p className="text-xs text-muted-foreground">
                {loading ? 'Loading…' : `${filtered.length} order${filtered.length === 1 ? '' : 's'}${selectedSp !== 'all' ? ` for ${selectedSp}` : ' across all reps'}`}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={selectedSp} onValueChange={setSelectedSp}>
              <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="sp-filter-select">
                <SelectValue placeholder="All salespersons" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Salespersons</SelectItem>
                {salespersons.map(sp => (
                  <SelectItem key={sp} value={sp}>{sp}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search CO / customer / branch"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-9 w-full md:w-60 pl-8 text-sm"
                data-testid="sp-search-input"
              />
            </div>
            <Button
              onClick={handleExport}
              disabled={exporting}
              className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="sp-export-excel-btn"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />}
              Export Excel
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-4 space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            {search ? 'No orders match your search' : 'No orders found'}
          </div>
        ) : (
          <>
            {/* Header row */}
            <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 bg-muted/40 border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              <div className="col-span-2">CO #</div>
              <div className="col-span-3">Customer / Branch</div>
              <div className="col-span-2">Salesperson</div>
              <div className="col-span-1 text-right">Pcs</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1 text-right">Delivery</div>
              <div className="col-span-1"></div>
            </div>
            <div className="max-h-[520px] overflow-y-auto" data-testid="sp-orders-list">
              {grouped ? (
                grouped.map(([sp, list]) => (
                  <div key={sp}>
                    <div className="px-3 py-2 bg-amber-50/60 dark:bg-amber-500/10 border-b border-border flex items-center justify-between">
                      <p className="text-xs font-bold text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5" />
                        {sp}
                      </p>
                      <span className="text-[10px] text-muted-foreground">{list.length} order{list.length === 1 ? '' : 's'}</span>
                    </div>
                    {list.map(renderRow)}
                  </div>
                ))
              ) : (
                filtered.map(renderRow)
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Main Component ───
const SalesDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [months, setMonths] = useState('12');
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  const [targetMonth, setTargetMonth] = useState('');
  const [targetForm, setTargetForm] = useState({ revenue: 0, carats_ordered: 0, orders_created: 0, carats_dispatched: 0 });
  const [savingTarget, setSavingTarget] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await analyticsApi.getSalesDashboard({ months: parseInt(months, 10) });
      setData(res.data);
      if (res.data.targets) {
        setTargetForm({
          revenue: res.data.targets.revenue || 0,
          carats_ordered: res.data.targets.carats_ordered || 0,
          orders_created: res.data.targets.orders_created || 0,
          carats_dispatched: res.data.targets.carats_dispatched || 0,
        });
      }
    } catch (err) {
      if (err?.response?.status === 403) toast.error('Access denied — Super Admin only');
      else toast.error('Failed to load sales dashboard');
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveTargets = async () => {
    const monthKey = targetMonth || data?.current_month?.key;
    if (!monthKey) return;
    setSavingTarget(true);
    try {
      await analyticsApi.setSalesTarget(monthKey, targetForm);
      toast.success(`Targets saved for ${formatMonth(monthKey)}`);
      setTargetDialogOpen(false);
      fetchData();
    } catch { toast.error('Failed to save targets'); }
    finally { setSavingTarget(false); }
  };

  const openTargetDialog = (monthKey) => {
    const mk = monthKey || data?.current_month?.key || '';
    setTargetMonth(mk);
    const ym = (data?.yearly_months || []).find(m => m.month === mk);
    setTargetForm({
      revenue: ym?.target_revenue || data?.targets?.revenue || 0,
      carats_ordered: ym?.target_carats_ordered || data?.targets?.carats_ordered || 0,
      orders_created: ym?.target_orders_created || data?.targets?.orders_created || 0,
      carats_dispatched: ym?.target_carats_dispatched || data?.targets?.carats_dispatched || 0,
    });
    setTargetDialogOpen(true);
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen" data-testid="sales-dashboard-page">
        <Header title="Sales Dashboard" />
        <div className="p-5 space-y-4 max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-48 rounded-2xl" />)}
          </div>
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      </div>
    );
  }

  const monthly = data.monthly || [];
  const daily = data.daily || [];
  const today = data.today || {};
  const week = data.week || {};
  const mom = data.mom_growth || {};
  const cm = data.current_month || {};
  const targets = data.targets || {};
  const hasTargets = targets && (targets.revenue > 0 || targets.orders_created > 0 || targets.carats_ordered > 0 || targets.carats_dispatched > 0);
  const yearlyMonths = data.yearly_months || [];
  const yearlyTotals = data.yearly_totals || {};
  const fyLabel = data.fy_label || 'FY';

  return (
    <div className="min-h-screen" data-testid="sales-dashboard-page">
      <Header title="Sales Dashboard" onRefresh={fetchData} />

      <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">

        {/* ═══════ TARGETS SECTION ═══════ */}
        <div className="animate-fade-in">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-amber-500" />
              <h2 className="font-heading text-lg font-bold">
                {formatMonth(cm.key)} Targets
              </h2>
            </div>
            <Button variant="outline" size="sm" onClick={() => openTargetDialog(cm.key)} data-testid="set-targets-btn"
              className="border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-500/30 dark:text-amber-400 dark:hover:bg-amber-500/10">
              <Settings2 className="h-3.5 w-3.5 mr-1.5" /> Set Targets
            </Button>
          </div>

          {hasTargets ? (
            <Card className="overflow-hidden border-amber-200/50 dark:border-amber-500/10">
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 justify-items-center">
                  <ProgressRing
                    value={cm.revenue} max={targets.revenue}
                    label="Revenue" current={`₹${formatCurrency(cm.revenue)}`} target={`₹${formatCurrency(targets.revenue)}`} unit=""
                    color="#10b981" delay={0}
                  />
                  <ProgressRing
                    value={cm.orders_created} max={targets.orders_created}
                    label="Orders" current={cm.orders_created} target={targets.orders_created} unit=""
                    color="#6366f1" delay={100}
                  />
                  <ProgressRing
                    value={cm.carats_ordered} max={targets.carats_ordered}
                    label="Carats In" current={`${cm.carats_ordered.toFixed(1)}`} target={`${targets.carats_ordered}`} unit="ct"
                    color="#8b5cf6" delay={200}
                  />
                  <ProgressRing
                    value={cm.carats_dispatched} max={targets.carats_dispatched}
                    label="Carats Out" current={`${cm.carats_dispatched.toFixed(1)}`} target={`${targets.carats_dispatched}`} unit="ct"
                    color="#ec4899" delay={300}
                  />
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-amber-300 dark:border-amber-500/20">
              <CardContent className="p-8 text-center">
                <Target className="h-10 w-10 text-amber-400/40 mx-auto mb-3" />
                <p className="text-sm font-medium text-muted-foreground">No targets set for {formatMonth(cm.key)}</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => openTargetDialog(cm.key)}>
                  <Target className="h-3.5 w-3.5 mr-1.5" /> Set Now
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* ═══════ YEARLY CARATS IN HERO ═══════ */}
        {yearlyMonths.length > 0 && (
          <div className="animate-fade-in" style={{ animationDelay: '50ms' }}>
            <Card className="overflow-hidden border-violet-200/50 dark:border-violet-500/10">
              <CardContent className="p-5 md:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
                    <Gem className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-bold">{fyLabel} — Carats In</h2>
                    <p className="text-xs text-muted-foreground">Year-to-date vs target</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                  <div className="p-3 rounded-xl bg-violet-50 dark:bg-violet-500/10 border border-violet-200 dark:border-violet-500/20">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-violet-600 dark:text-violet-400">Carats In (YTD)</p>
                    <p className="text-2xl md:text-3xl font-black text-violet-700 dark:text-violet-300 tabular-nums">{yearlyTotals.carats_ordered?.toFixed(1)}ct</p>
                    {yearlyTotals.target_carats_ordered > 0 && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">Target: {yearlyTotals.target_carats_ordered.toFixed(1)}ct ({yearlyTotals.target_carats_ordered > 0 ? Math.round((yearlyTotals.carats_ordered / yearlyTotals.target_carats_ordered) * 100) : 0}%)</p>
                    )}
                  </div>
                  <div className="p-3 rounded-xl bg-pink-50 dark:bg-pink-500/10 border border-pink-200 dark:border-pink-500/20">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-pink-600 dark:text-pink-400">Carats Out (YTD)</p>
                    <p className="text-2xl md:text-3xl font-black text-pink-700 dark:text-pink-300 tabular-nums">{yearlyTotals.carats_dispatched?.toFixed(1)}ct</p>
                    {yearlyTotals.target_carats_dispatched > 0 && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">Target: {yearlyTotals.target_carats_dispatched.toFixed(1)}ct</p>
                    )}
                  </div>
                  <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400">Revenue (YTD)</p>
                    <p className="text-2xl md:text-3xl font-black text-emerald-700 dark:text-emerald-300 tabular-nums">Rs {formatCurrency(yearlyTotals.revenue)}</p>
                    {yearlyTotals.target_revenue > 0 && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">Target: ₹{formatCurrency(yearlyTotals.target_revenue)}</p>
                    )}
                  </div>
                  <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-blue-600 dark:text-blue-400">Orders (YTD)</p>
                    <p className="text-2xl md:text-3xl font-black text-blue-700 dark:text-blue-300 tabular-nums">{yearlyTotals.orders_created}</p>
                    {yearlyTotals.target_orders_created > 0 && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">Target: {yearlyTotals.target_orders_created}</p>
                    )}
                  </div>
                </div>

                {/* Month-by-month breakdown table */}
                <div className="overflow-x-auto rounded-lg border">
                  <table className="w-full text-xs" data-testid="yearly-breakdown-table">
                    <thead>
                      <tr className="bg-muted/40 border-b">
                        <th className="text-left p-2 font-bold text-[10px] uppercase tracking-wider text-muted-foreground">Month</th>
                        <th className="text-right p-2 font-bold text-[10px] uppercase tracking-wider text-violet-600 dark:text-violet-400">Carats In</th>
                        <th className="text-right p-2 font-bold text-[10px] uppercase tracking-wider text-violet-400 dark:text-violet-600">Target</th>
                        <th className="text-right p-2 font-bold text-[10px] uppercase tracking-wider text-pink-600 dark:text-pink-400">Carats Out</th>
                        <th className="text-right p-2 font-bold text-[10px] uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Revenue</th>
                        <th className="text-right p-2 font-bold text-[10px] uppercase tracking-wider text-blue-600 dark:text-blue-400">Orders</th>
                        <th className="text-right p-2 font-bold text-[10px] uppercase tracking-wider text-muted-foreground">Pcs In</th>
                        <th className="text-right p-2 font-bold text-[10px] uppercase tracking-wider text-muted-foreground">Pcs Out</th>
                      </tr>
                    </thead>
                    <tbody>
                      {yearlyMonths.map((m, i) => {
                        const isCurrent = m.month === cm.key;
                        const pct = m.target_carats_ordered > 0 ? Math.round((m.carats_ordered / m.target_carats_ordered) * 100) : null;
                        return (
                          <tr key={m.month} className={cn("border-b last:border-0 transition-colors",
                            isCurrent ? "bg-violet-50/50 dark:bg-violet-500/5 font-semibold" : "hover:bg-muted/30"
                          )}>
                            <td className="p-2 font-medium">
                              {formatMonth(m.month)}
                              {isCurrent && <Badge variant="outline" className="ml-1.5 text-[8px] py-0 px-1 border-violet-300 text-violet-600 dark:text-violet-400">NOW</Badge>}
                            </td>
                            <td className="p-2 text-right tabular-nums font-bold text-violet-700 dark:text-violet-300">
                              {m.carats_ordered.toFixed(1)}ct
                              {pct !== null && (
                                <span className={cn("ml-1 text-[9px] font-normal", pct >= 100 ? "text-emerald-600" : pct >= 70 ? "text-amber-600" : "text-red-500")}>
                                  ({pct}%)
                                </span>
                              )}
                            </td>
                            <td className="p-2 text-right tabular-nums text-muted-foreground">{m.target_carats_ordered > 0 ? `${m.target_carats_ordered.toFixed(1)}ct` : '—'}</td>
                            <td className="p-2 text-right tabular-nums text-pink-600 dark:text-pink-400">{m.carats_dispatched.toFixed(1)}ct</td>
                            <td className="p-2 text-right tabular-nums text-emerald-600 dark:text-emerald-400">Rs {formatCurrency(m.revenue)}</td>
                            <td className="p-2 text-right tabular-nums text-blue-600 dark:text-blue-400">{m.orders_created}</td>
                            <td className="p-2 text-right tabular-nums text-muted-foreground">{m.pieces_ordered}</td>
                            <td className="p-2 text-right tabular-nums text-muted-foreground">{m.pieces_dispatched}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-muted/60 font-bold border-t-2">
                        <td className="p-2">TOTAL</td>
                        <td className="p-2 text-right tabular-nums text-violet-700 dark:text-violet-300">{yearlyTotals.carats_ordered?.toFixed(1)}ct</td>
                        <td className="p-2 text-right tabular-nums text-muted-foreground">{yearlyTotals.target_carats_ordered > 0 ? `${yearlyTotals.target_carats_ordered.toFixed(1)}ct` : '—'}</td>
                        <td className="p-2 text-right tabular-nums text-pink-600 dark:text-pink-400">{yearlyTotals.carats_dispatched?.toFixed(1)}ct</td>
                        <td className="p-2 text-right tabular-nums text-emerald-600 dark:text-emerald-400">Rs {formatCurrency(yearlyTotals.revenue)}</td>
                        <td className="p-2 text-right tabular-nums text-blue-600 dark:text-blue-400">{yearlyTotals.orders_created}</td>
                        <td className="p-2 text-right tabular-nums text-muted-foreground">{yearlyTotals.pieces_ordered}</td>
                        <td className="p-2 text-right tabular-nums text-muted-foreground">{yearlyTotals.pieces_dispatched}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ═══════ TODAY + THIS WEEK ═══════ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Today */}
          <Card className="animate-slide-in border-blue-200/50 dark:border-blue-500/10" data-testid="today-snapshot">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                  <Zap className="h-4 w-4 text-white" />
                </div>
                <CardTitle className="font-heading text-base">Today</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'Orders', value: today.orders_created || 0, color: 'text-blue-600 dark:text-blue-400' },
                  { label: 'Pieces In', value: today.pieces_ordered || 0, color: 'text-indigo-600 dark:text-indigo-400' },
                  { label: 'Pieces Out', value: today.pieces_dispatched || 0, color: 'text-emerald-600 dark:text-emerald-400' },
                  { label: 'Carats In', value: `${(today.carats_ordered || 0).toFixed(1)}ct`, color: 'text-violet-600 dark:text-violet-400' },
                  { label: 'Carats Out', value: `${(today.carats_dispatched || 0).toFixed(1)}ct`, color: 'text-pink-600 dark:text-pink-400' },
                  { label: 'Revenue', value: `₹${formatCurrency(today.revenue || 0)}`, color: 'text-emerald-600 dark:text-emerald-400' },
                ].map(s => (
                  <div key={s.label} className="p-2.5 rounded-lg bg-muted/40 hover:bg-muted/60 transition-colors">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{s.label}</p>
                    <p className={cn("text-base font-extrabold tabular-nums", s.color)}>{s.value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* This Week */}
          <Card className="animate-slide-in border-purple-200/50 dark:border-purple-500/10" style={{ animationDelay: '100ms' }} data-testid="week-snapshot">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
                  <CalendarDays className="h-4 w-4 text-white" />
                </div>
                <CardTitle className="font-heading text-base">This Week</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'Orders', value: week.orders_created || 0, color: 'text-blue-600 dark:text-blue-400' },
                  { label: 'Pieces In', value: week.pieces_ordered || 0, color: 'text-indigo-600 dark:text-indigo-400' },
                  { label: 'Pieces Out', value: week.pieces_dispatched || 0, color: 'text-emerald-600 dark:text-emerald-400' },
                  { label: 'Carats In', value: `${(week.carats_ordered || 0).toFixed(1)}ct`, color: 'text-violet-600 dark:text-violet-400' },
                  { label: 'Carats Out', value: `${(week.carats_dispatched || 0).toFixed(1)}ct`, color: 'text-pink-600 dark:text-pink-400' },
                  { label: 'Revenue', value: `₹${formatCurrency(week.revenue || 0)}`, color: 'text-emerald-600 dark:text-emerald-400' },
                ].map(s => (
                  <div key={s.label} className="p-2.5 rounded-lg bg-muted/40 hover:bg-muted/60 transition-colors">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{s.label}</p>
                    <p className={cn("text-base font-extrabold tabular-nums", s.color)}>{s.value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ═══════ INSIGHTS ROW ═══════ */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* MoM Growth */}
          <Card className="animate-slide-in" style={{ animationDelay: '150ms' }}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg bg-emerald-100 dark:bg-emerald-500/15 flex items-center justify-center">
                  <TrendingUp className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <CardTitle className="text-sm font-bold">Month-over-Month</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0 space-y-1.5">
              <GrowthBadge value={mom.revenue} label="Revenue" />
              <GrowthBadge value={mom.orders} label="Orders" />
              <GrowthBadge value={mom.carats_ordered} label="Carats In" />
              <GrowthBadge value={mom.carats_dispatched} label="Carats Out" />
            </CardContent>
          </Card>

          {/* Best / Worst Days */}
          <Card className="animate-slide-in" style={{ animationDelay: '200ms' }}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg bg-amber-100 dark:bg-amber-500/15 flex items-center justify-center">
                  <Star className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                </div>
                <CardTitle className="text-sm font-bold">Last 30 Days Highlights</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              <DayCard day={data.best_day} label="Most Orders" icon={Crown} iconColor="text-amber-500"
                metric={data.best_day?.orders_created || 0} metricLabel="orders" />
              <DayCard day={data.best_revenue_day} label="Top Revenue" icon={IndianRupee} iconColor="text-emerald-500"
                metric={`₹${formatCurrency(data.best_revenue_day?.revenue || 0)}`} metricLabel="" />
              <DayCard day={data.worst_day} label="Slowest Day" icon={Clock} iconColor="text-slate-400"
                metric={data.worst_day?.orders_created || 0} metricLabel="orders" />
            </CardContent>
          </Card>

          {/* Customer Concentration */}
          <Card className="animate-slide-in" style={{ animationDelay: '250ms' }}>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg bg-red-100 dark:bg-red-500/15 flex items-center justify-center">
                  <AlertTriangle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                </div>
                <CardTitle className="text-sm font-bold">Customer Concentration</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-center py-3">
                <p className={cn("text-4xl font-black tabular-nums animate-count-up",
                  data.concentration_pct > 70 ? "text-red-600 dark:text-red-400" :
                  data.concentration_pct > 50 ? "text-amber-600 dark:text-amber-400" :
                  "text-emerald-600 dark:text-emerald-400"
                )}>{data.concentration_pct}%</p>
                <p className="text-xs text-muted-foreground mt-1">of revenue from top 3 customers</p>
                <Badge className={cn("mt-2 text-[10px]",
                  data.concentration_pct > 70 ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400" :
                  data.concentration_pct > 50 ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400" :
                  "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                )}>
                  {data.concentration_pct > 70 ? 'High Risk' : data.concentration_pct > 50 ? 'Moderate' : 'Healthy'}
                </Badge>
              </div>
              {(data.top_customers || []).slice(0, 3).map((c, i) => (
                <div key={c.customer} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/40 transition-colors">
                  <span className="text-xs font-bold text-amber-600 w-5">#{i+1}</span>
                  <span className="text-xs flex-1 truncate">{c.customer}</span>
                  <span className="text-xs font-bold tabular-nums text-emerald-600 dark:text-emerald-400">Rs {formatCurrency(c.revenue)}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* ═══════ DAILY CHART ═══════ */}
        <Card className="animate-slide-in" style={{ animationDelay: '300ms' }}>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <TrendingUp className="h-4 w-4 text-white" />
              </div>
              <div>
                <CardTitle className="font-heading text-base">Daily Activity — Last 30 Days</CardTitle>
                <p className="text-xs text-muted-foreground">Orders created & pieces dispatched per day</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="h-[240px]" data-testid="daily-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={daily} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-neutral-700" />
                  <XAxis dataKey="date" tickFormatter={formatDay} tick={{ fontSize: 10 }} interval={4} />
                  <YAxis tick={{ fontSize: 11 }} width={30} />
                  <Tooltip content={<ChartTooltip formatter={formatDay} />} />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Bar dataKey="orders_created" name="Orders" fill="#6366f1" radius={[3,3,0,0]} />
                  <Bar dataKey="pieces_dispatched" name="Dispatched" fill="#10b981" radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* ═══════ MONTHLY CHARTS ═══════ */}
        <div className="flex items-center gap-3 animate-slide-in" style={{ animationDelay: '350ms' }}>
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger className="w-[140px]" data-testid="sales-range-selector"><SelectValue /></SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map(o => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">Monthly chart range</span>
        </div>

        {/* Revenue + KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 animate-slide-in" style={{ animationDelay: '400ms' }}>
          <Card className="group hover:shadow-lg transition-all hover:-translate-y-0.5">
            <CardContent className="p-4 md:p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Total Revenue</p>
                  <p className="text-2xl md:text-3xl font-black text-foreground tabular-nums">Rs {formatCurrency(data.total_revenue)}</p>
                  <p className="text-xs text-muted-foreground">From {data.total_invoices} invoices</p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <IndianRupee className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="group hover:shadow-lg transition-all hover:-translate-y-0.5">
            <CardContent className="p-4 md:p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Invoices</p>
                  <p className="text-2xl md:text-3xl font-black text-foreground tabular-nums">{data.total_invoices}</p>
                  <p className="text-xs text-muted-foreground">Converted from memos</p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FileCheck className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="group hover:shadow-lg transition-all hover:-translate-y-0.5">
            <CardContent className="p-4 md:p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Pending Memos</p>
                  <p className="text-2xl md:text-3xl font-black text-foreground tabular-nums">{data.total_approval_memos}</p>
                  <p className="text-xs text-muted-foreground">Awaiting conversion</p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FileText className="h-5 w-5 text-amber-700 dark:text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Area + Pieces Bar Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="animate-slide-in" style={{ animationDelay: '450ms' }}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg bg-emerald-100 dark:bg-emerald-500/15 flex items-center justify-center">
                  <IndianRupee className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <CardTitle className="text-sm font-bold">Revenue Trend</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="h-[220px]" data-testid="revenue-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthly} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-neutral-700" />
                    <XAxis dataKey="month" tickFormatter={formatMonth} tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={v => formatCurrency(v)} tick={{ fontSize: 10 }} width={55} />
                    <Tooltip content={<ChartTooltip formatter={formatMonth} />} />
                    <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#10b981" fill="url(#revGrad)" strokeWidth={2.5} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="animate-slide-in" style={{ animationDelay: '500ms' }}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg bg-blue-100 dark:bg-blue-500/15 flex items-center justify-center">
                  <Package className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                </div>
                <CardTitle className="text-sm font-bold">Pieces Ordered vs Dispatched</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="h-[220px]" data-testid="pieces-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthly} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-slate-200 dark:stroke-neutral-700" />
                    <XAxis dataKey="month" tickFormatter={formatMonth} tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={35} />
                    <Tooltip content={<ChartTooltip formatter={formatMonth} />} />
                    <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
                    <Bar dataKey="pieces_ordered" name="Ordered" fill="#3b82f6" radius={[3,3,0,0]} />
                    <Bar dataKey="pieces_dispatched" name="Dispatched" fill="#10b981" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Top Customers */}
          <Card className="animate-slide-in" style={{ animationDelay: '550ms' }}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg bg-amber-100 dark:bg-amber-500/15 flex items-center justify-center">
                  <Crown className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                </div>
                <CardTitle className="text-sm font-bold">Top Customers</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <ScrollArea className="h-[240px]">
                {(data.top_customers || []).length > 0 ? (
                  <div className="space-y-1">
                    {data.top_customers.map((c, i) => (
                      <div key={c.customer} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors">
                        <div className={cn("h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0",
                          i === 0 ? "bg-amber-100 dark:bg-amber-500/20" : i === 1 ? "bg-slate-200 dark:bg-slate-500/20" : "bg-orange-100 dark:bg-orange-500/20"
                        )}>
                          <span className="text-[10px] font-black">{i+1}</span>
                        </div>
                        <span className="text-sm font-medium flex-1 truncate">{c.customer}</span>
                        <span className="text-sm font-bold tabular-nums text-emerald-600 dark:text-emerald-400">Rs {formatCurrency(c.revenue)}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-center text-sm text-muted-foreground py-8">No data yet</p>}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Pending Approvals */}
          <Card className="animate-slide-in" style={{ animationDelay: '600ms' }}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-lg bg-orange-100 dark:bg-orange-500/15 flex items-center justify-center">
                  <Clock className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
                </div>
                <CardTitle className="text-sm font-bold">Pending Approval Memos</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <ScrollArea className="h-[240px]">
                {(data.pending_approvals || []).length > 0 ? (
                  <div className="space-y-1">
                    {data.pending_approvals.map((m, i) => (
                      <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/orders/${m.order_id}`)}>
                        <div className="h-7 w-7 rounded-lg bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                          <FileText className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold">{m.co_number}</p>
                          <p className="text-[10px] text-muted-foreground truncate">{m.customer_name}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px]">{m.pieces} pcs</Badge>
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-emerald-600 dark:text-emerald-400 font-medium py-8">
                    All memos converted
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* ═══════ ORDERS BY SALESPERSON ═══════ */}
        <OrdersBySalespersonSection />
      </div>

      {/* ═══════ SET TARGETS DIALOG ═══════ */}
      <Dialog open={targetDialogOpen} onOpenChange={setTargetDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Target className="h-5 w-5 text-amber-500" />
              Set Targets — {formatMonth(targetMonth || cm.key)}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Month</Label>
              <Select value={targetMonth || cm.key} onValueChange={(v) => {
                setTargetMonth(v);
                const ym = (data?.yearly_months || []).find(m => m.month === v);
                if (ym) setTargetForm({ revenue: ym.target_revenue || 0, carats_ordered: ym.target_carats_ordered || 0, orders_created: ym.target_orders_created || 0, carats_dispatched: ym.target_carats_dispatched || 0 });
              }}>
                <SelectTrigger data-testid="target-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {yearlyMonths.map(m => (
                    <SelectItem key={m.month} value={m.month}>{formatMonth(m.month)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Revenue Target (Rs)</Label>
              <Input type="number" min="0" value={targetForm.revenue} onChange={e => setTargetForm(f => ({...f, revenue: parseFloat(e.target.value) || 0}))} data-testid="target-revenue" />
            </div>
            <div className="space-y-2">
              <Label>Orders Created Target</Label>
              <Input type="number" min="0" value={targetForm.orders_created} onChange={e => setTargetForm(f => ({...f, orders_created: parseInt(e.target.value) || 0}))} data-testid="target-orders" />
            </div>
            <div className="space-y-2">
              <Label>Carats Ordered Target</Label>
              <Input type="number" min="0" step="0.1" value={targetForm.carats_ordered} onChange={e => setTargetForm(f => ({...f, carats_ordered: parseFloat(e.target.value) || 0}))} data-testid="target-carats-in" />
            </div>
            <div className="space-y-2">
              <Label>Carats Dispatched Target</Label>
              <Input type="number" min="0" step="0.1" value={targetForm.carats_dispatched} onChange={e => setTargetForm(f => ({...f, carats_dispatched: parseFloat(e.target.value) || 0}))} data-testid="target-carats-out" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setTargetDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveTargets} disabled={savingTarget} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="save-targets-btn">
              {savingTarget ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Target className="h-4 w-4 mr-1" />}
              Save Targets
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesDashboard;
